import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import {  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  BeachAccess as BeachAccessIcon,
  Celebration as CelebrationIcon,
  CameraAlt as CameraIcon,  ContactPhone as ContactIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  ReportProblem as ReportProblemIcon,
  Paid as PaidIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { employeePortalAPI, employeePayrollAPI } from '../utils/api';
import type { EmployeeMeResponse, AttendanceDay } from '../types/employeePortal';
import type { EmployeePortalException, EmployeePortalPayslip } from '../types/payroll';
import AbsenceReasonModal from '../components/AbsenceReasonModal';
import EmployeePayrollExceptionExplainModal from '../components/EmployeePayrollExceptionExplainModal';
import EmployeeLayout from '../components/EmployeeLayout';
import EmployeeShiftPanel from '../components/EmployeeShiftPanel';
import EmployeeNotificationSettings from '../components/EmployeeNotificationSettings';
import EmployeePhotos from '../components/EmployeePhotos';
import { formatMoney, formatPayType } from '../utils/payrollFormat';
import { formatDateOnly, getTodayDateInCompanyTimezone } from '../utils/timezone';

const employeeAccent = {
  main: 'var(--an-primary)',
  dark: 'var(--an-primary-dark)',
  secondary: 'var(--an-accent)',
  soft: 'var(--an-primary-light)',
};

const panelSx = {
  borderRadius: 'var(--an-radius-md)',
  border: '1px solid var(--an-border-light)',
  boxShadow: 'var(--an-shadow-sm)',
  backgroundColor: 'var(--an-surface)',
};

const panelHeaderSx = {
  p: 2.25,
  backgroundColor: 'var(--an-surface)',
  borderBottom: '1px solid var(--an-border-light)',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const statCardSx = {
  height: '100%',
  borderRadius: 'var(--an-radius-md)',
  border: '1px solid var(--an-border-light)',
  boxShadow: 'var(--an-shadow-sm)',
  backgroundColor: 'var(--an-surface)',
  transition: 'transform var(--an-transition), box-shadow var(--an-transition), border-color var(--an-transition)',
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: 'rgb(var(--an-primary-rgb) / 0.24)',
    boxShadow: 'var(--an-shadow-md)',
  },
};

const EmployeeDashboard: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EmployeeMeResponse['data'] | null>(null);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [absenceModalOpen, setAbsenceModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<AttendanceDay | null>(null);

  const [payrollExceptions, setPayrollExceptions] = useState<EmployeePortalException[]>([]);
  const [payrollPayslips, setPayrollPayslips] = useState<EmployeePortalPayslip[]>([]);
  const [selectedPayrollEx, setSelectedPayrollEx] = useState<EmployeePortalException | null>(null);
  const [payrollExModalOpen, setPayrollExModalOpen] = useState(false);

  const handleOpenAbsenceModal = (day: AttendanceDay) => {
    setSelectedDay(day);
    setAbsenceModalOpen(true);
  };

  const handleCloseAbsenceModal = () => {
    setAbsenceModalOpen(false);
    setSelectedDay(null);
  };

  const handleAbsenceReasonSuccess = () => {
    handleCloseAbsenceModal();
    fetchData();
  };

  const { dateFrom, dateTo } = React.useMemo(() => {
    if (!selectedMonth) return { dateFrom: '', dateTo: '' };
    const [year, month] = selectedMonth.split('-').map(Number);
    const start = new Date(year, month - 1, 1);
    const now = new Date();
    
    let end;
    if (year === now.getFullYear() && (month - 1) === now.getMonth()) {
      end = now;
    } else {
      end = new Date(year, month, 0);
    }

    const format = (d: Date) => {
      const y = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${mm}-${dd}`;
    };

    return {
      dateFrom: format(start),
      dateTo: format(end)
    };
  }, [selectedMonth]);

  const monthRangeTitle = React.useMemo(() => {
    if (!selectedMonth) return '';
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const date = new Date(year, month - 1, 1);
      const locale = i18n.language || 'en';
      return date.toLocaleString(locale, { month: 'long', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }, [selectedMonth, i18n.language]);

  const formatPortalMoney = useCallback((amount: number, currency?: string | null) => {
    return formatMoney(amount, currency || 'AED', i18n.language || 'en');
  }, [i18n.language]);

  const formatPortalPayType = useCallback((payType: 'hourly' | 'monthly' | null | undefined) => {
    return formatPayType(payType, (key) => t(`employeePortal.${key}`));
  }, [t]);

  const fetchData = useCallback(async () => {
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setError(null);
      const response = await employeePortalAPI.getMe({
        date_from: dateFrom,
        date_to: dateTo
      });
      if (response.success) {
        setData(response.data);
      } else {
        setError(t('employeePortal.fetchDataError'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('employeePortal.fetchDataError'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchPayrollData = useCallback(async () => {
    try {
      const exRes = await employeePayrollAPI.getMyPayrollExceptions(1, 20);
      if (exRes.success) {
        const opens = (exRes.data.items || []).filter((e: any) => e.status === 'open');
        setPayrollExceptions(opens);
      }
    } catch (err: any) {
      if (err?.response?.status !== 403) { /* ignore */ }
    }
    try {
      const psRes = await employeePayrollAPI.getMyPayslips(1, 3);
      if (psRes.success) setPayrollPayslips(psRes.data.items || []);
    } catch (err: any) {
      if (err?.response?.status !== 403) { /* ignore */ }
    }
  }, []);

  useEffect(() => { fetchPayrollData(); }, [fetchPayrollData]);

  const handlePayrollExSuccess = useCallback(() => {
    setPayrollExModalOpen(false);
    setSelectedPayrollEx(null);
    fetchPayrollData();
  }, [fetchPayrollData]);

  const getStatusColor = (status: string) => {
    switch (normalizeStatus(status)) {
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'vacation': return 'info';
      case 'holiday': return 'success';
      case 'insufficient hours': return 'warning';
      case 'insufficient detections': return 'warning';
      case 'not working day': return 'default';
      default: return 'default';
    }
  };


  const getStatusIcon = (status: string) => {
    switch (normalizeStatus(status)) {
      case 'present': return <CheckCircleIcon fontSize="small" />;
      case 'absent': return <CancelIcon fontSize="small" />;
      case 'vacation': return <BeachAccessIcon fontSize="small" />;
      case 'holiday': return <CelebrationIcon fontSize="small" />;
      case 'insufficient hours': return <i className="bi bi-arrow-clockwise"></i>;
      case 'insufficient detections': return <ReportProblemIcon fontSize="small" />;
      default: return null;
    }
  };

  const normalizeStatus = (status: string) => status.toLowerCase().replace(/[_-]+/g, ' ').trim();

  const getStatusTranslationKey = (status: string) => {
    const normalized = normalizeStatus(status);

    switch (normalized) {
      case 'not working day':
        return 'notWorkingDay';
      case 'insufficient hours':
        return 'insufficientHours';
      case 'insufficient detections':
        return 'insufficientDetections';
      case 'no calendar':
        return 'noCalendar';
      case 'holiday':
        return 'holiday';
      default:
        return normalized;
    }
  };

  const getPPETypeTranslation = (type: string) => {
    // Map backend snake_case keys to i18n camelCase keys in common.json
    const mapping: Record<string, string> = {
      'hairnet': 'hairnet',
      'gloves': 'gloves',
      'helmet': 'helmet',
      'industrial_gloves': 'industrialGloves',
      'industrial_jacket': 'industrialJacket',
    };
    
    const key = mapping[type] || type;
    return tc(`ppeTypes.${key}`, { defaultValue: type.replace(/_/g, ' ') });
  };

  if (loading && !data) {
    return (
      <EmployeeLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress sx={{ color: employeeAccent.main }} />
        </Box>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout>
      <Box sx={{ mb: 4, position: 'relative' }}>
        {loading && data && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100dvh',
              bgcolor: 'rgb(var(--an-surface-rgb) / 0.60)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              backdropFilter: 'blur(2px)',
            }}
          >
            <CircularProgress size={60} thickness={4} sx={{ color: employeeAccent.main }} />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: 'var(--an-text-primary)', mb: 0.5 }}>
              {t('employeePortal.dashboard')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--an-text-secondary)', fontWeight: 700 }}>
              {monthRangeTitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="month"
              label={t('employeePortal.selectMonth')}
              size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 190,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 'var(--an-radius-sm)',
                  backgroundColor: 'var(--an-surface)',
                },
                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                  borderColor: employeeAccent.main,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: employeeAccent.main,
                },
              }}
            />
            <IconButton
              onClick={fetchData}
              disabled={loading}
              sx={{
                width: 40,
                height: 40,
                color: employeeAccent.main,
                backgroundColor: 'var(--an-surface)',
                border: '1px solid var(--an-border-light)',
                borderRadius: 'var(--an-radius-sm)',
                '&:hover': { backgroundColor: employeeAccent.soft },
              }}
            >
              <i className="bi bi-arrow-clockwise"></i>
            </IconButton>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {data && (
          <>
            <Alert 
              severity="info" 
              icon={<InfoIcon fontSize="medium" />}
              sx={{ 
                mb: 4, 
                borderRadius: 'var(--an-radius-md)',
                p: 2,
                '& .MuiAlert-message': {
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                backgroundColor: employeeAccent.soft,
                color: employeeAccent.main,
                border: '1px solid var(--an-border-light)',
                borderLeft: `4px solid ${employeeAccent.main}`,
                boxShadow: 'var(--an-shadow-sm)'
              }}
            >
              {t('employeePortal.dataShownFor', { month: monthRangeTitle })}
            </Alert>

            <Grid container spacing={3}>
              {/* Profile Card */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Card elevation={0} sx={{ ...panelSx, height: '100%', overflow: 'hidden' }}>
                  <CardContent sx={{ textAlign: 'center', pt: 4 }}>
                    <Avatar
                      src={data.profile.profile_image || undefined}
                      sx={{ width: 120, height: 120, mx: 'auto', mb: 2, border: `4px solid ${employeeAccent.soft}` }}
                    >
                      {!data.profile.profile_image && <PersonIcon sx={{ fontSize: 60 }} />}
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold">{data.profile.name}</Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>{data.profile.position}</Typography>
                    <Chip label={data.profile.emp_id} size="small" sx={{ mt: 1, mb: 3 }} />
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    <Stack spacing={2} sx={{ textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <BusinessIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">{t('employeePortal.department')}</Typography>
                          <Typography variant="body2" fontWeight="medium">{data.profile.department_name}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EventIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">{t('employeePortal.calendar')}</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {(data.profile.calendar_name?.toLowerCase() === 'no calendar' || data.profile.calendar_name === 'N/A') 
                              ? tc('messages.noCalendar') 
                              : (data.profile.calendar_name || '-')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ContactIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">{t('employeePortal.email')} / {t('employeePortal.phone')}</Typography>
                          <Typography variant="body2" fontWeight="medium">{data.profile.email || '-'}</Typography>
                          <Typography variant="body2" fontWeight="medium">{data.profile.phone || '-'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EventIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">{t('employeePortal.hireDate')}</Typography>
                          <Typography variant="body2" fontWeight="medium">{data.profile.hire_date || '-'}</Typography>
                        </Box>
                      </Box>
                      {data.profile.end_of_hire_date && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <EventIcon color="error" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="textSecondary" display="block">{t('employeePortal.endOfHireDate')}</Typography>
                            <Typography variant="body2" fontWeight="medium">{data.profile.end_of_hire_date}</Typography>
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CameraIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">{t('employeePortal.lastDetection')}</Typography>
                          <Typography variant="body2" fontWeight="medium">{data.profile.last_detection || '-'}</Typography>
                        </Box>
                      </Box>
                      {data.profile.pay_type && (
                        <>
                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PaidIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="textSecondary" display="block">{t('employeePortal.compensation')}</Typography>
                              <Typography variant="body2" fontWeight="medium">{formatPortalPayType(data.profile.pay_type)}</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {data.profile.pay_type === 'hourly'
                                  ? formatPortalMoney(Number(data.profile.hourly_rate || 0), data.profile.currency)
                                  : formatPortalMoney(Number(data.profile.monthly_salary || 0), data.profile.currency)}
                              </Typography>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Stack>

                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<i className="bi bi-key"></i>}
                      onClick={() => navigate('/employee/change-password')}
                      sx={{
                        mt: 4,
                        textTransform: 'none',
                        color: employeeAccent.main,
                        borderColor: employeeAccent.main,
                        '&:hover': {
                          borderColor: employeeAccent.dark,
                          backgroundColor: employeeAccent.soft,
                        },
                      }}
                    >
                      {t('employeePortal.changePassword')}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Summary & Attendance */}
              <Grid size={{ xs: 12, md: 9 }}>
                {data.payroll_summary && (
                  <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden', mb: 3 }}>
                    <Box sx={panelHeaderSx}>
                      <PaidIcon fontSize="small" sx={{ color: employeeAccent.main }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">{t('employeePortal.payrollSummary')}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('employeePortal.payrollPeriod', {
                            from: data.payroll_summary.period.from,
                            to: data.payroll_summary.period.to
                          })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      {data.payroll_summary.warning === 'no_working_days_in_month' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {t('employeePortal.noWorkingDaysInMonth')}
                        </Alert>
                      )}
                      {data.payroll_summary.hourly && (
                        <Grid container spacing={2}>
                          {[
                            { label: t('employeePortal.hourlyRate'), value: formatPortalMoney(data.payroll_summary.hourly.hourly_rate, data.payroll_summary.currency) },
                            { label: t('employeePortal.totalHours'), value: data.payroll_summary.hourly.total_hours.toFixed(2) },
                            { label: t('employeePortal.earnedSoFar'), value: formatPortalMoney(data.payroll_summary.hourly.earned_so_far, data.payroll_summary.currency) },
                          ].map((item) => (
                            <Grid size={{ xs: 12, sm: 4 }} key={item.label}>
                              <Typography variant="caption" color="textSecondary" display="block">{item.label}</Typography>
                              <Typography variant="h6" fontWeight="bold">{item.value}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      {data.payroll_summary.monthly && (
                        <Grid container spacing={2}>
                          {[
                            { label: t('employeePortal.expectedSalary'), value: formatPortalMoney(data.payroll_summary.monthly.expected_salary, data.payroll_summary.currency) },
                            { label: t('employeePortal.earnedSoFar'), value: formatPortalMoney(data.payroll_summary.monthly.earned_so_far, data.payroll_summary.currency) },
                            ...(data.payroll_summary.monthly.working_days_in_month !== undefined
                              ? [{ label: t('employeePortal.workingDaysInMonth'), value: data.payroll_summary.monthly.working_days_in_month }]
                              : []),
                            ...(data.payroll_summary.monthly.paid_day_units !== undefined
                              ? [{ label: t('employeePortal.paidDayUnits'), value: data.payroll_summary.monthly.paid_day_units }]
                              : []),
                          ].map((item) => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.label}>
                              <Typography variant="caption" color="textSecondary" display="block">{item.label}</Typography>
                              <Typography variant="h6" fontWeight="bold">{item.value}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  </Paper>
                )}

                {payrollExceptions.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {t('employeeDashboard.payroll.exceptions.warning', { count: payrollExceptions.length })}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {payrollExceptions.slice(0, 5).map(ex => (
                        <Box key={ex.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                          <Typography variant="body2">{formatDateOnly(ex.work_date)} – {ex.type}</Typography>
                          <Button size="small" variant="contained" onClick={() => { setSelectedPayrollEx(ex); setPayrollExModalOpen(true); }} sx={{ textTransform: 'none' }}>
                            {t('employeeDashboard.payroll.exceptions.explain')}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Alert>
                )}

                {payrollPayslips.length > 0 && (
                  <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden', mb: 3 }}>
                    <Box sx={panelHeaderSx}>
                      <PaidIcon fontSize="small" sx={{ color: employeeAccent.main }} />
                      <Typography variant="h6" fontWeight="bold">{t('employeeDashboard.payroll.myPayslips')}</Typography>
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" onClick={() => navigate('/employee/payslips')} sx={{ textTransform: 'none' }}>{t('employeeDashboard.payroll.viewAll')}</Button>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {payrollPayslips.slice(0, 3).map(ps => (
                          <Grid key={ps.id} size={{ xs: 12, sm: 4 }}>
                            <Card variant="outlined" sx={{ borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 2 } }} onClick={() => navigate(`/employee/payslips/${ps.id}`)}>
                              <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="text.secondary">{t('employeeDashboard.payroll.period')}</Typography>
                                <Typography variant="body2" fontWeight={700}>{formatDateOnly((ps as any).period_start || '')} – {formatDateOnly((ps as any).period_end || '')}</Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>{formatPortalMoney(ps.net, ps.currency)}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                )}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: t('employeePortal.totalDetections'), value: data.attendance_summary.total_detections, icon: <CameraIcon />, color: employeeAccent.secondary },
                    { label: t('employeePortal.daysPresent'), value: data.attendance_summary.days_present, icon: <CheckCircleIcon />, color: employeeAccent.main },
                    { label: t('employeePortal.daysAbsent'), value: data.attendance_summary.days_absent, icon: <CancelIcon />, color: 'var(--an-error)' },
                    { label: t('employeePortal.daysVacation'), value: data.attendance_summary.days_vacation, icon: <BeachAccessIcon />, color: 'var(--an-info)' },
                    { label: t('employeePortal.daysHoliday'), value: data.attendance_summary.days_holiday || 0, icon: <CelebrationIcon />, color: 'var(--an-accent)' },
                    { label: t('employeePortal.daysInsufficientDetections'), value: data.attendance_summary.days_insufficient_detections || 0, icon: <ReportProblemIcon />, color: 'var(--an-warning)' },
                  ].map((stat, idx) => (
                    <Grid size={{ xs: 6, sm: 4, md: 2 }} key={idx}>
                      <Card elevation={0} sx={statCardSx}>
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <Box sx={{ color: stat.color, mb: 1 }}>{stat.icon}</Box>
                          <Typography variant="h5" fontWeight="bold">{stat.value}</Typography>
                          <Typography variant="caption" color="textSecondary">{stat.label}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
                  <Box sx={panelHeaderSx}>
                    <DateRangeIcon fontSize="small" sx={{ color: employeeAccent.main }} />
                    <Typography variant="h6" fontWeight="bold">{t('employeePortal.dailyAttendance')}</Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'var(--an-surface-muted)' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.date')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.status')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.checkIn')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.checkOut')}</TableCell>
                          {!isMobile && (
                            <>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.breakStart')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.breakEnd')}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.detections')}</TableCell>
                            </>
                          )}
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.action')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.attendance_summary.daily.length > 0 ? (
                          data.attendance_summary.daily.map((day, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{day.date}</TableCell>
                              <TableCell>
                                <Chip
                                  label={t(`employeePortal.${getStatusTranslationKey(day.status)}`, { defaultValue: day.status })}
                                  color={getStatusColor(day.status) as any}
                                  size="small"
                                  icon={getStatusIcon(day.status) as any}
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell>{day.check_in || '-'}</TableCell>
                              <TableCell>{day.check_out || '-'}</TableCell>
                              {!isMobile && (
                                <>
                                  <TableCell>{day.break_in || '-'}</TableCell>
                                  <TableCell>{day.break_out || '-'}</TableCell>
                                  <TableCell align="center">{day.total_detections}</TableCell>
                                </>
                              )}
                              <TableCell>
                                {normalizeStatus(day.status) === 'absent' ? (
                                  day.absence_reason ? (
                                    <Chip
                                      label={t(`employeePortal.reasonType.${day.absence_reason.reason_type}`)}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleOpenAbsenceModal(day)}
                                      sx={{ cursor: 'pointer', borderColor: employeeAccent.main, color: employeeAccent.main, '&:hover': { bgcolor: 'rgb(var(--an-primary-rgb) / 0.08)' } }}
                                    />
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleOpenAbsenceModal(day)}
                                      sx={{ textTransform: 'none', bgcolor: employeeAccent.main, fontSize: '0.75rem' }}
                                    >
                                      {t('employeePortal.submitReason')}
                                    </Button>
                                  )
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={isMobile ? 5 : 8} align="center" sx={{ py: 3 }}>
                              <Typography color="textSecondary">{t('employeePortal.noDailyData')}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* PPE Violations */}
              {data.profile.is_ppe_enabled && (
                <Grid size={12}>
                  <Paper elevation={0} sx={{ ...panelSx, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 24, bgcolor: 'var(--an-error)', borderRadius: 1 }} />
                      {t('employeePortal.ppeViolations')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                      <Card variant="outlined" sx={{ minWidth: 150, borderRadius: 'var(--an-radius-md)', bgcolor: 'var(--an-error-light)' }}>
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight="bold" color="error">{data.ppe_summary.total_violations}</Typography>
                          <Typography variant="caption" color="textSecondary">{t('employeePortal.totalViolations')}</Typography>
                        </CardContent>
                      </Card>
                      {Object.entries(data.ppe_summary.by_type)
                        .filter(([_, count]) => count > 0)
                        .map(([type, count]) => (
                        <Card key={type} variant="outlined" sx={{ minWidth: 120, borderRadius: 'var(--an-radius-md)' }}>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h6" fontWeight="bold">{count}</Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                              {getPPETypeTranslation(type)}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Vacations */}
              {data.vacations && data.vacations.length > 0 && (
                <Grid size={12}>
                  <Paper elevation={0} sx={{ ...panelSx, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 24, bgcolor: 'var(--an-info)', borderRadius: 1 }} />
                      {t('employeePortal.vacations')}
                    </Typography>

                    {(() => {
                      const today = getTodayDateInCompanyTimezone(data.profile.timezone || 'UTC');
                      const upcoming = data.vacations.filter(v => v.from_date >= today);
                      const past = data.vacations.filter(v => v.from_date < today);

                      return (
                        <>
                          {upcoming.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="subtitle1" fontWeight="bold" color="primary">{t('employeePortal.upcomingVacations')}</Typography>
                              <TableContainer sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.fromDate')}</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.toDate')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {upcoming.map(vac => (
                                      <TableRow key={vac.id}>
                                        <TableCell>{formatDateOnly(vac.from_date)}</TableCell>
                                        <TableCell>{formatDateOnly(vac.to_date)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                          {past.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">{t('employeePortal.pastVacations')}</Typography>
                              <TableContainer sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.fromDate')}</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>{t('employeePortal.toDate')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {past.map(vac => (
                                      <TableRow key={vac.id}>
                                        <TableCell>{formatDateOnly(vac.from_date)}</TableCell>
                                        <TableCell>{formatDateOnly(vac.to_date)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Paper>
                </Grid>
              )}
            </Grid>

            {data?.profile?.shifts_enabled && (
              <>
                <EmployeeShiftPanel timezone={data.profile.timezone} />
                <EmployeeNotificationSettings />
              </>
            )}
            <Box sx={{ mt: 3 }}>
              <EmployeePhotos />
            </Box>
          </>
        )}
      </Box>

      <AbsenceReasonModal
        open={absenceModalOpen}
        onClose={handleCloseAbsenceModal}
        day={selectedDay}
        onSubmitSuccess={handleAbsenceReasonSuccess}
      />
      <EmployeePayrollExceptionExplainModal
        open={payrollExModalOpen}
        onClose={() => { setPayrollExModalOpen(false); setSelectedPayrollEx(null); }}
        exception={selectedPayrollEx}
        onSuccess={handlePayrollExSuccess}
      />
    </EmployeeLayout>
  );
};

export default EmployeeDashboard;

